import { Box, Checkbox, Chip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { columnsLearningContinuum } from '../../utils.js'
import { renderElementsTableLC } from '../../utils.jsx'
import DataGrid from '../../../../table/DataGrid'
import { updateColumnTableCurriculum } from '../../../../../store/actions/datatable.actions'
import { useParams } from 'react-router-dom'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import isEmpty from 'lodash/isEmpty'
import FilterButtonLearningContinuum from '../../LearningContinuum/Filter/Filter'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import PreviewLearningContinuum from './PreviewLearningContinuum'

const AddLearningContinuum = ({ onClose, open, lcsIds = [], onAdd }) => {
  const { curriculumId } = useParams()
  const dispatch = useDispatch()
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})

  const { isLoading, isLoadingList, getLearningContinuum } = useLearningContinuum()

  const list = useSelector((state) => (state?.lc ? state?.lc[curriculumId]?.list || [] : []))
  const totalList = useSelector((state) => (state?.lc ? state?.lc[curriculumId]?.totalList || 0 : 0))

  const [checkIds, setCheckIds] = useState(lcsIds)
  const [showDialog, setShowDialog] = useState(null)

  const state = useSelector((state) => state?.filter?.lc)
  const skip = useSelector((state) => state?.datatable?.lc?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.lc?.rowsPerPage || 50)
  const columnsTable = useSelector((state) => state?.datatable?.lc?.[curriculumId] || [])

  useEffect(() => {
    if (!isEmpty(state?.filter)) {
      const filterData = {}
      if (!isEmpty(state?.filter?.lcElements)) {
        filterData['lcElements'] = state?.filter?.lcElements
      }
      if (!isEmpty(state?.filter?.curriculumElements)) {
        filterData['curriculumElements'] = state?.filter?.curriculumElements
      }
      if (!isEmpty(filterData)) {
        filterData.lcIds = lcsIds
        getLearningContinuum(curriculumId, skip, rowsPerPage, filterData)
      }
    }
  }, [curriculumId, rowsPerPage, skip, state, state?.filter])

  useEffect(() => {
    const columns = columnsLearningContinuum(currentCurriculum, list)
    dispatch(updateColumnTableCurriculum(currentCurriculum._id, 'lc', columns, true))
  }, [currentCurriculum])

  const onConfirm = async (e) => {
    e.preventDefault()
    const lcs = list.filter((row) => checkIds.includes(row._id))
    onAdd(lcs)
    onClose()
  }

  const onSelectedAll = (checked) => {
    if (checked) {
      setCheckIds(list.map((row) => row._id))
    } else {
      setCheckIds([])
    }
  }
  const onSelected = (id, checked) => {
    if (checked) {
      setCheckIds([...checkIds, id])
    } else {
      setCheckIds(checkIds.filter((item) => item !== id))
    }
  }

  return (
    <DialogConfirmCancel
      id={`add-learning-continuum`}
      open={open}
      disabledConfirm={isLoading}
      onCancel={onClose}
      onConfirm={onConfirm}
      title='Add learning Continuum'
      onCloseHeader={true}
      maxWidth='lg'
    >
      <div className='h-screen-modal-content-footer overflow-auto'>
        <Box className='px-4 py-2 h-10 border-b border-b-2 border-primary text-primary-foreground flex items-center justify-between'>
          <FilterButtonLearningContinuum />
        </Box>
        <Box className='absolute top-20 bottom-4 inset-x-4'>
          {showDialog?.type === 'preview' && (
            <PreviewLearningContinuum
              open={showDialog.type === 'preview'}
              onClose={() => setShowDialog(null)}
              formData={showDialog.form}
            />
          )}
          <DataGrid
            id='learning-continuum'
            nameModule='lc'
            curriculumId={curriculumId}
            rows={list
              .filter((row) => !lcsIds.includes(row._id))
              .map((row, index) => {
                const elements = renderElementsTableLC(row, currentCurriculum, 'lc')
                return {
                  ...row,
                  index: index + 1 + rowsPerPage * skip,
                  ...elements,
                  version: (
                    <Chip
                      label={`v${row.version || '1'}`}
                      size='small'
                      color='primary'
                      className='font-bold'
                    />
                  ),
                  checkIds: (
                    <Checkbox
                      id={`checkbox-id-user-${row._id}`}
                      checked={checkIds.includes(row._id)}
                      onChange={(e) => onSelected(row._id, e.target.checked)}
                      color='primary'
                    />
                  ),
                  actions: (
                    <div className='flex items-center gap-1 items-center justify-center'>
                      <IconTooltipButton
                        id={`preview-${row._id}`}
                        title='Preview'
                        icon='expand-alt'
                        onClick={() =>
                          setShowDialog({
                            type: 'preview',
                            form: JSON.parse(
                              JSON.stringify({
                                ...row,
                                granularSteps: row.granularSteps
                                  ? row.granularSteps
                                  : row.levels.map((level) => {
                                      return {
                                        id: `level-${level.levelId}-gs-0`,
                                        levelId: level.levelId,
                                        isLevel: true,
                                        definition: level.definition,
                                        workedExamples: level.workedExamples || [],
                                        teachingSuggestions: level.teachingSuggestions || []
                                      }
                                    })
                              })
                            )
                          })
                        }
                      />
                    </div>
                  )
                }
              })}
            loading={isLoadingList}
            totalRows={totalList}
            skip={skip}
            rowsPerPage={rowsPerPage}
            columns={[
              { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
              {
                show: true,
                key: 'checkIds',
                align: 'center',
                width: 15,
                label: (
                  <Checkbox
                    id={`checkbox-id-user-header`}
                    checked={checkIds.length > 0 && checkIds.length === totalList}
                    indeterminate={checkIds.length > 0 && checkIds.length < totalList}
                    onChange={(e) => onSelectedAll(e.target.checked)}
                    color='primary'
                  />
                )
              },
              ...columnsTable,
              { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
            ]}
            labelNoRows="You don't have any learning continuum yet"
          />
        </Box>
      </div>
    </DialogConfirmCancel>
  )
}

export default AddLearningContinuum
